import type { Recipe } from '@platewell/core';
import { useConnectionStatus, useRevisions, useStore } from '@platewell/query';
import { useState } from 'react';
import { Dialog } from './dialog.js';

const FIELD_LABELS: Record<string, string> = {
  title: 'title',
  description: 'description',
  ingredients: 'ingredients',
  instructions: 'steps',
  photos: 'photos',
  cookDates: 'cook dates',
  nutrition: 'nutrition',
  tags: 'tags',
  notes: 'notes',
  source: 'source',
  servings: 'servings',
  times: 'times',
  timing: 'times',
};

/**
 * History for one recipe.
 *
 * Restoring writes the old values forward as a new edit rather than rewinding the document.
 * Rewinding a CRDT is not well defined once other devices have diverged, and a forward edit
 * is also the honest thing to show collaborators: the recipe changed, again, just now.
 */
export function RevisionDrawer({ recipe, onClose }: { recipe: Recipe; onClose: () => void }) {
  const { revisions, loading, error } = useRevisions(recipe.id);
  const { readOnly, online } = useConnectionStatus();
  const store = useStore();
  const [restoring, setRestoring] = useState<number | null>(null);

  async function restore(revisionNo: number) {
    setRestoring(revisionNo);
    try {
      await store.restoreRevision(recipe.id, revisionNo);
      onClose();
    } finally {
      setRestoring(null);
    }
  }

  return (
    <Dialog onClose={onClose} aria-label="History">
      <h2>History</h2>

      {!online && (
        <p className="hint">
          History lives on the server, so it is unavailable while you are offline. Your recipes
          are not — everything else here keeps working.
        </p>
      )}

      {loading && <p className="hint">Loading history…</p>}
      {error != null && <p className="error-text">{error.message}</p>}

      {!loading && error == null && revisions.length === 0 && (
        <p className="hint">No earlier versions yet. Edits are grouped, so a burst of changes
          becomes a single entry a few minutes after you stop typing.</p>
      )}

      {revisions.map((revision) => (
        <div className="revision" key={revision.revisionNo}>
          <span className="when">{formatWhen(revision.changedAt)}</span>
          <span style={{ flex: 1 }}>
            {revision.changedFields.length === 0
              ? 'Edited'
              : `Changed ${revision.changedFields
                  .map((field) => FIELD_LABELS[field] ?? field)
                  .join(', ')}`}
          </span>
          {!readOnly && (
            <button
              className="btn ghost"
              disabled={restoring != null}
              onClick={() => void restore(revision.revisionNo)}
            >
              {restoring === revision.revisionNo ? 'Restoring…' : 'Restore'}
            </button>
          )}
        </div>
      ))}

      <div className="row" style={{ marginTop: 18 }}>
        <span className="spacer" />
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    </Dialog>
  );
}

function formatWhen(timestamp: number): string {
  return new Date(timestamp).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
